import { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/stores/RootStore';
import { authApi } from '@/services/authApi';
import { TERMS_OF_SERVICE, PRIVACY_POLICY, type LegalDoc } from '@/data/legalContent';
import LegalModal from './LegalModal';
import AuthShell from './AuthShell';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { scrollToFirstError } from './formError';
import s from './Auth.module.scss';

// [2026-09-12] 검증 실패 시 첫 에러 필드 판정용 — 화면 위→아래 순서(Signup과 동일 패턴).
const FIELD_ORDER = ['phone', 'terms'];

/**
 * [2026-09-11 #4] 소셜 신규가입 완료단계 (autonics sns/{provider}/mberCheck 참고).
 *   소셜 최초 로그인 → 백엔드가 status=PENDING + needsCompletion:true → SocialCallback이 여기로 라우팅.
 *   본인인증(휴대폰 SMS·기존 PhoneVerifyService 재사용) + 필수/선택 약관 명시동의 + 개인정보 유효기간 선택 →
 *   /me/complete-social(PENDING 토큰 auth)로 확정 → status=ACTIVE. 완료 후 auth.restore로 갱신.
 */
const OTP_SECONDS = 180;
// 개인정보 유효기간 — autonics(1/3/5년/탈퇴시)와 동일. 값=개월(0=탈퇴시까지). 백엔드 normalizeRetention과 일치.
const RETENTIONS = [
  { v: 0, label: '회원 탈퇴 시까지' },
  { v: 12, label: '1년' },
  { v: 36, label: '3년' },
  { v: 60, label: '5년' },
];
const mmss = (sec: number) => `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

function SocialComplete() {
  const auth = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [timer, setTimer] = useState(0);
  const [phoneMsg, setPhoneMsg] = useState('');
  const [t, setT] = useState({ terms: false, privacy: false, age14: false, sms: false, email: false });
  const [retention, setRetention] = useState(0);
  const [modalDoc, setModalDoc] = useState<LegalDoc | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);   // [2026-09-12] 가입완료 = 네이티브 alert 대신 인페이지 모달
  const [fe, setFe] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);   // [2026-09-12] 첫 에러 필드 스크롤+포커스 대상

  useEffect(() => {
    if (!otpSent || verified || timer <= 0) return;
    const id = setInterval(() => setTimer((v) => (v <= 1 ? 0 : v - 1)), 1000);
    return () => clearInterval(id);
  }, [otpSent, verified, timer]);

  // 로그인(PENDING) 상태가 아니면 접근 불가 — 소셜 로그인 직후에만 도달.
  if (auth.ready && !auth.isAuthed) return <Navigate to="/login" replace />;

  const allAgree = t.terms && t.privacy && t.age14 && t.sms && t.email;
  const toggleAll = (v: boolean) => setT({ terms: v, privacy: v, age14: v, sms: v, email: v });

  async function sendOtp() {
    setPhoneMsg('');
    const r = await authApi.phoneSend(phone, 'SIGNUP');
    if (!r.success) { setPhoneMsg(r.message || '발송 실패'); return; }
    setOtpSent(true); setVerified(false); setTimer(OTP_SECONDS);
    setPhoneMsg('인증번호를 발송했습니다. 3분 이내 입력해주세요.');
  }
  async function verifyOtp() {
    const r = await authApi.phoneVerify(phone, code, 'SIGNUP');
    if (!r.success) { setPhoneMsg(r.message || '인증 실패'); return; }
    setVerified(true); setPhoneMsg('휴대폰 인증이 완료되었습니다.');
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setErr(null);
    const fx: Record<string, string> = {};
    if (!verified) fx.phone = '휴대폰 본인인증을 완료해주세요.';
    if (!(t.terms && t.privacy && t.age14)) fx.terms = '필수 약관에 동의해주세요.';
    setFe(fx);
    // [2026-09-12] 에러 있으면 첫 빨간 필드로 스크롤+포커스(formError.ts 참고).
    if (Object.keys(fx).length > 0) { scrollToFirstError(formRef.current, FIELD_ORDER, fx); return; }
    setBusy(true);
    const r = await authApi.completeSocial({
      phone, age14: t.age14, agreeTerms: t.terms, agreePrivacy: t.privacy,
      smsOpt: t.sms, emailOpt: t.email, retentionMonths: retention,
    });
    setBusy(false);
    if (!r.success) return setErr(r.message || '가입 완료에 실패했습니다.');
    await auth.restore();   // status=ACTIVE 반영(사용자 정보 갱신)
    setDone(true);          // [2026-09-12] 완료 모달 → [시작하기]에서 홈 이동
  }

  const providerLabel = auth.user?.socialProvider === 'naver' ? '네이버' : '구글';

  return (
    <AuthShell eyebrow="EVS MEMBERSHIP" title={<>소셜 가입 완료</>} image="/images/auth/complete-hero.jpg"
      desc={`${providerLabel} 계정으로 시작합니다. 서비스 이용을 위해 아래 본인인증·필수 동의·개인정보 유효기간을 완료해주세요.`}>
      <p className={s.hint} style={{ marginBottom: 18 }}>표시(<span className={s.req}>*</span>)는 필수 입력 항목입니다.</p>
      {err && <div className={`${s.msg} ${s.error}`}>{err}</div>}

      <form onSubmit={submit} ref={formRef}>
        {/* 휴대폰 본인인증 — 기존 SMS OTP(PhoneVerifyService) 재사용 */}
        <div className={s.field}>
          <label className={s.label}>휴대폰 본인인증<span className={s.req}>*</span></label>
          <div className={s.inline}>
            <input name="phone" className={`${s.input} ${fe.phone ? s.err : ''}`} value={phone}
              onChange={(e) => { setPhone(e.target.value); if (fe.phone) setFe((f) => ({ ...f, phone: '' })); }}
              placeholder="휴대폰 번호 ('-' 없이)" inputMode="numeric" disabled={verified} />
            <button type="button" className={s.inlineBtn} onClick={sendOtp} disabled={!phone || verified}>
              {otpSent ? '재전송' : '인증 전송'}</button>
          </div>
          {otpSent && !verified && (
            <div className={s.inline} style={{ marginTop: 8 }}>
              <input className={s.input} value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="인증번호 6자리" inputMode="numeric" maxLength={6} />
              <button type="button" className={s.inlineBtn} onClick={verifyOtp} disabled={!code}>확인</button>
            </div>
          )}
          {otpSent && !verified && timer > 0 && <p className={s.hint}>남은 시간 <span className={s.timer}>{mmss(timer)}</span></p>}
          {phoneMsg && <p className={`${s.hint} ${verified ? s.ok : s.bad}`}>{phoneMsg}</p>}
          {fe.phone && !phoneMsg && <p className={`${s.hint} ${s.bad}`}>{fe.phone}</p>}
        </div>

        {/* 약관 동의 */}
        <div className={s.terms} data-field="terms">
          <div className={`${s.termRow} ${s.termAll}`}>
            <input type="checkbox" id="all" checked={allAgree} onChange={(e) => toggleAll(e.target.checked)} />
            <label htmlFor="all">필수·선택 항목에 모두 동의합니다.</label>
          </div>
          <div className={s.termRow}>
            <input type="checkbox" id="tos" checked={t.terms} onChange={(e) => setT({ ...t, terms: e.target.checked })} />
            <label htmlFor="tos"><span className={s.tag}>[필수]</span> 이용약관 동의</label>
            <button type="button" className={s.detailBtn} onClick={() => setModalDoc(TERMS_OF_SERVICE)}>자세히 보기</button>
          </div>
          <div className={s.termRow}>
            <input type="checkbox" id="pp" checked={t.privacy} onChange={(e) => setT({ ...t, privacy: e.target.checked })} />
            <label htmlFor="pp"><span className={s.tag}>[필수]</span> 개인정보 처리방침 동의</label>
            <button type="button" className={s.detailBtn} onClick={() => setModalDoc(PRIVACY_POLICY)}>자세히 보기</button>
          </div>
          <div className={s.termRow}>
            <input type="checkbox" id="age" checked={t.age14} onChange={(e) => setT({ ...t, age14: e.target.checked })} />
            <label htmlFor="age"><span className={s.tag}>[필수]</span> 만 14세 이상입니다.</label>
          </div>
          <div className={s.termRow}>
            <input type="checkbox" id="sms" checked={t.sms} onChange={(e) => setT({ ...t, sms: e.target.checked })} />
            <label htmlFor="sms"><span className={s.tag}>[선택]</span> SMS 수신 동의</label>
          </div>
          <div className={s.termRow}>
            <input type="checkbox" id="eml" checked={t.email} onChange={(e) => setT({ ...t, email: e.target.checked })} />
            <label htmlFor="eml"><span className={s.tag}>[선택]</span> 이메일 수신 동의</label>
          </div>
        </div>
        {fe.terms && <p className={`${s.hint} ${s.bad}`} style={{ marginTop: -12, marginBottom: 16 }}>{fe.terms}</p>}

        {/* 개인정보 유효기간 */}
        <div className={s.field}>
          <label className={s.label}>개인정보 유효기간<span className={s.req}>*</span></label>
          <div className={s.retRow}>
            {RETENTIONS.map((o) => (
              <button type="button" key={o.v}
                className={`${s.retBtn} ${retention === o.v ? s.retOn : ''}`}
                onClick={() => setRetention(o.v)}>{o.label}</button>
            ))}
          </div>
        </div>

        <button className={s.primary} disabled={busy}>{busy ? '가입 완료 중…' : '가입 완료'}</button>
      </form>
      <LegalModal doc={modalDoc} onClose={() => setModalDoc(null)} />
      <ConfirmModal open={done} alertMode title="가입 완료" confirmText="시작하기"
        message="가입이 완료되었습니다. EVS와 함께 시작하세요." onConfirm={() => nav('/', { replace: true })} onCancel={() => {}} />
    </AuthShell>
  );
}

export default observer(SocialComplete);
